import { useMemo, useState } from "react";
import { z } from "zod";

import { Direction, Flex } from "@ntemarked/mittne-components/dist/Flex/Flex";

import LoadingAnimation from "components/organisms/LoadingAnimation";
import { MainLayoutPageHeader } from "components/organisms/MainLayout";
import Form from "components/organisms/form/Form";
import FormButton from "components/organisms/form/FormButton";
import SelectField, { SelectFieldClean } from "components/organisms/form/SelectField";
import TextField from "components/organisms/form/TextField";
import {
	useSendForbruksstoppNotificationMutation,
	useSendSpotpriceNotificationMutation,
	useSendWeekreportNotificationMutation,
} from "pages/Features/Send push/SendNotificationsPage.graphql-gen";
import { notificationTypeToName, SectionTitle } from "pages/Features/Statistikk/StatisticsPage";
import { MeterDetailsFragment, useGetUserQuery } from "pages/Users/Users.graphql-gen";

import { NordpoolArea, NotificationType, SpotpriceLevelOperator } from "generated/graphql-types";

const notificationTypeOptions = [NotificationType.FORBRUKSTOPP, NotificationType.SPOTPRICE, NotificationType.WEEKREPORT];
const spotOptions = [
	{ value: SpotpriceLevelOperator.OVER, label: "Høy" },
	{ value: SpotpriceLevelOperator.UNDER, label: "Lav" },
];

function getMeterName(meter: MeterDetailsFragment): string {
	const name = meter.alias ?? meter.address?.streetAddress;
	return name ?? "Ukjent";
}

export const NotificationsSchema = z.object({
	userId: z.string().optional(),
	measuringpointId: z.string().optional(),
	week: z.coerce.number().min(1, { message: "Ukenummer må være mellom 1 og 53" }).max(53, { message: "Ukenummer må være mellom 1 og 53" }).optional(),
	operator: z.string().optional(),
	area: z.string().optional(),
});

export function SendNotificationsPage() {
	const [userId, setUserId] = useState<string>("");
	const [type, setCurrentType] = useState<NotificationType>(NotificationType.WEEKREPORT);
	const [selectedOperator, setSelectedOperator] = useState<SpotpriceLevelOperator>(SpotpriceLevelOperator.OVER);
	const [success, setSuccess] = useState<boolean>(false);

	const [sendWeekReport, { error: errorWeekreport }] = useSendWeekreportNotificationMutation();
	const [sendForbrukstopp, { error: errorForbrukstopp }] = useSendForbruksstoppNotificationMutation();
	const [sendSpotprice, { error: errorSpotprice }] = useSendSpotpriceNotificationMutation();
	const [manualError, setManualError] = useState<boolean>(false);

	const error = errorForbrukstopp ?? errorWeekreport ?? errorSpotprice ?? manualError;

	const { data, loading: userLoading } = useGetUserQuery({
		variables: {
			userId: userId ?? "",
		},
		skip: !userId || userId.trim() === "",
	});

	const selectOptions = notificationTypeOptions.map((o) => ({
		value: o,
		label: notificationTypeToName(o) ?? "",
	}));

	const defaultMeter = useMemo(() => data?.user?.meters?.at(0)?.meteringPointId, [data?.user]);

	function onSuccess() {
		setSuccess(true);
		setManualError(false);
	}

	function onFailure() {
		setSuccess(false);
		setManualError(true);
	}

	return (
		<div>
			<MainLayoutPageHeader title={"Send varsler"} />
			<SectionTitle>Bruker</SectionTitle>
			<TextField label="Bruker id" onChange={(e) => setUserId(e.currentTarget.value)} />
			{userId && !userLoading && <p>{data?.user?.name ? "Valgt bruker: " + data?.user?.name : "Fant ikke bruker"}</p>}
			{userLoading && !data?.user && <LoadingAnimation />}
			{data?.user && (
				<>
					<SectionTitle>Varseltype</SectionTitle>
					<SelectFieldClean
						defaultValue={type}
						options={selectOptions}
						label="Varseltype"
						name="type"
						onChange={(v) => setCurrentType(v as NotificationType)}
					/>
					<SectionTitle>Parametre</SectionTitle>
					<Form
						onSubmit={async (data) => {
							if (type === NotificationType.WEEKREPORT) {
								sendWeekReport({
									variables: {
										measuringpointId: data.measuringpointId || defaultMeter,
										week: data.week,
										userId,
									},
								}).then((r) => {
									if (r.data?.sendWeekreportNotification) onSuccess();
									else onFailure();
								});
							}
							if (type === NotificationType.FORBRUKSTOPP) {
								sendForbrukstopp({
									variables: {
										measuringpointId: data.measuringpointId || defaultMeter,
										userId,
									},
								}).then((r) => {
									if (r.data?.sendForbruksstoppNotification) onSuccess();
									else onFailure();
								});
							}
							if (type === NotificationType.SPOTPRICE) {
								sendSpotprice({
									variables: {
										area: data.area as NordpoolArea,
										operator: selectedOperator,
										userId,
									},
								}).then((r) => {
									if (r.data?.sendSpotpriceNotification) onSuccess();
									else onFailure();
								});
							}
						}}
						schema={NotificationsSchema}
					>
						<Flex gap="1rem" direction={Direction.column}>
							{type === NotificationType.WEEKREPORT && (
								<>
									<TextField label="Ukenummer" name="week" type="number" />
									<SelectField
										defaultValue={defaultMeter}
										options={data?.user?.meters?.map((m) => {
											return {
												value: m?.meteringPointId ?? "Ukjent",
												label: getMeterName(m),
											};
										})}
										label="Bolig"
										name="measuringpointId"
									/>
								</>
							)}
							{type === NotificationType.FORBRUKSTOPP && (
								<SelectField
									defaultValue={defaultMeter}
									options={data?.user?.meters?.map((m) => {
										return {
											value: m?.meteringPointId ?? "",
											label: getMeterName(m),
										};
									})}
									label="Bolig"
									name="measuringpointId"
								/>
							)}
							{type === NotificationType.SPOTPRICE && (
								<>
									{spotOptions.map((option) => (
										<label key={option.value}>
											<input
												type="radio"
												name="operator"
												value={option.value}
												checked={selectedOperator === option.value}
												onChange={(e) => setSelectedOperator(e.target.value as SpotpriceLevelOperator)}
											/>
											{option.label}
										</label>
									))}
									<SelectField
										defaultValue={"NO3"}
										options={[1, 2, 3, 4, 5].map((r) => {
											return {
												value: "NO" + r,
												label: "NO" + r,
											};
										})}
										label="Sone"
										name="area"
									/>
								</>
							)}
						</Flex>
						<FormButton style={{ marginTop: "3rem" }} label="Send" />
						{success && <p>Varsel sendt! Tidspunkt {new Date().toLocaleTimeString("nb-NO")}</p>}
						{error && <p>Noe gikk galt..</p>}
					</Form>
				</>
			)}
		</div>
	);
}
